import { AxiosError } from 'axios';
import { useCallback } from 'react';

import { s3ImageRepository } from '@/repositories/S3ImageRepository';
import { AppMessage } from '@/types/appMessage';
import * as S3ImageUpload from '@/types/s3imageUpload';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

/**
 * Custom hook for uploading images to S3.
 * Provides methods to get upload configuration and upload files.
 * @param addMessage - Function to add messages to the application context.
 * @param host - The host URL for the presigned config endpoint.
 */
const useUploadImage = ({
  addMessage,
  host
}: {
  addMessage(messages: AppMessage[]): void;
  host: string;
}) => {
  const getUploadConfig = useCallback(
    async (config: S3ImageUpload.PresignedFileUploadConfigPayload, personId: string) => {
      try {
        return await s3ImageRepository.getPresignedFileUploadConfig({ config, host, personId });
      } catch (error) {
        addMessage(mapAxiosErrorMessages(error as AxiosError));
        return null;
      }
    },
    [addMessage, host]
  );

  const uploadFile = useCallback(
    async (file: File, config: S3ImageUpload.PresignedUploadConfig) => {
      try {
        return await s3ImageRepository.uploadToS3({
          file: file,
          config
        });
      } catch (error) {
        addMessage(mapAxiosErrorMessages(error as AxiosError));
        return null;
      }
    },
    [addMessage]
  );

  return {
    getUploadConfig,
    uploadFile
  };
};

export default useUploadImage;
